import React, { useState, useCallback, useEffect, useMemo } from "react";
import Header from "../../../components/Header/Header";
import CompanySelector from "../../../components/Selector/CompanySelector";
import AuthInput from "../../../components/Input/AuthInput";
import {
  ErrorToast,
  SuccessToast,
  WarningToast,
} from "../../../utils/ShowToast";
import SearchableTable from "../../../components/Tables/SearchableTable";
import { AllStockColumns } from "../../../assets/Columns";
import { CreateManyCompanyInvoiceApi } from "../../../ApiRequests";
import { fetchStockStats } from "../../../store/Slices/StockStatsSlice";
import { useDispatch, useSelector } from "react-redux";
import {
  validateForm,
  defaultValidationRules,
  sanitizeInput,
} from "../../../utils/validation";
import { withErrorHandling } from "../../../utils/errorHandling";
import { checkInternetConnection } from "../../../utils/checkConnection";
import { fetchProducts } from "../../../store/Slices/ProductSlice";
import { fetchCustomers } from "../../../store/Slices/CustomerSlice";
import EditDraftStockModal from "../../../components/Modals/EditDraftStockModal";
import CustomerSelector from "../../../components/Selector/CustomerSelector";
import { toLocalYmd } from "../../../utils/dateTime";

const createDraftId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const ADD_STOCK_VALIDATION_RULES = {
  quantity: defaultValidationRules.quantity,
  date: defaultValidationRules.date,
};

const OFFLINE_SUBMIT_HINT =
  "Internet connection nahi mil rahi. Barah-e-karam apna connection check karain aur online hone ke baad dubara koshish karein.";

// Branch users must have branchId; admin (role 1) may omit it and use the item's branchId instead
const getUserData = () => {
  try {
    const userData = localStorage.getItem("user");
    if (!userData) {
      throw new Error("No user data found");
    }
    const parsed = JSON.parse(userData);
    if (parsed.role === 2 && !parsed.branchId) {
      throw new Error("Invalid user data structure");
    }
    return parsed;
  } catch (error) {
    console.error("Error retrieving user data:", error);
    ErrorToast("Session expired. Please login again.");
    window.location.href = "/login";
    return null;
  }
};

const findProductByInput = (products, value) => {
  if (!value || !Array.isArray(products)) return null;
  const query = value.toLowerCase();
  return (
    products.find(
      (item) =>
        item.name.toLowerCase() === query || item.code.toLowerCase() === query
    ) ?? null
  );
};

const isValidStockRow = (item) =>
  item.itemId && item.companyId && item.qty > 0 && item.total_amount > 0;

const resolveMongoId = (value) => {
  if (!value) return "";
  if (typeof value === "object" && value._id != null) return String(value._id);
  return String(value);
};

const normalizeStockRowForApi = (row) => {
  const {
    draftId,
    item,
    company,
    direct_customer,
    direct_customer_type,
    direct_qty: directQtyDisplay,
    sale_invoice_no,
    directSend,
    ...rest
  } = row;

  const normalized = {
    ...rest,
    itemId: resolveMongoId(rest.itemId),
    companyId: resolveMongoId(rest.companyId),
    branchId: resolveMongoId(rest.branchId),
  };

  if (directSend && Number(directSend.direct_qty) > 0) {
    normalized.directSend = {
      ...directSend,
      customerId: resolveMongoId(directSend.customerId),
      itemId: resolveMongoId(directSend.itemId || rest.itemId),
      name: directSend.name || item || "",
      unit: directSend.unit || "",
      purchase: Number(directSend.purchase ?? rest.purchase ?? 0),
    };
  }

  return normalized;
};

const ItemCodeField = ({ value, products, onChange, onBlur }) => (
  <div className="flex flex-col">
    <input
      type="text"
      id="ItemCode"
      list="Item-Code"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={(e) => onBlur(e.target.value)}
      placeholder="Select Item"
      className="px-3 py-2.5 outline-none rounded-lg border border-line bg-card text-ink text-sm focus:border-main focus:ring-2 focus:ring-main/15 col-span-2 max1000:col-span-1"
    />
    <datalist id="Item-Code" className="max-h-[50vh]">
      {products?.map((option) => (
        <option key={`${option._id}-name`} value={option.name} />
      ))}
      {products?.map((option) => (
        <option key={`${option._id}-code`} value={option.code} />
      ))}
    </datalist>
  </div>
);

const AddMultipleStock = () => {
  const [SelectedCompany, setSelectedCompany] = useState("");
  const [CurDate, setCurDate] = useState(
    toLocalYmd()
  );
  const [itemId, setItemId] = useState("");
  const [itemCode, setItemCode] = useState("");
  const [itemCompanyId, setItemCompanyId] = useState("");
  const [itemCompanyName, setItemCompanyName] = useState("");
  const [itemPurchase, setItemPurchase] = useState("");
  const [itemBranchId, setItemBranchId] = useState("");
  const [itemName, setItemName] = useState("");
  const [itemUnit, setItemUnit] = useState("");
  const [invoiceNo, setInvoiceNo] = useState("");
  const [truckNo, setTruckNo] = useState("");
  const [qty, setQty] = useState("");
  const [desc, setDesc] = useState("");
  const [directSendEnabled, setDirectSendEnabled] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [customerInvoiceNo, setCustomerInvoiceNo] = useState("");
  const [customerDesc, setCustomerDesc] = useState("");
  const [directQty, setDirectQty] = useState("");
  const [directPrice, setDirectPrice] = useState("");
  const [directPayment, setDirectPayment] = useState("");
  const [AllStockStock, setAllStockStock] = useState([]);
  const [SearchText, setSearchText] = useState("");
  const [Loading, setLoading] = useState(false);
  const [OpenEditModal, setOpenEditModal] = useState(false);
  const [Selected, setSelected] = useState(null);
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true
  );

  const dispatch = useDispatch();
  const ItemsState = useSelector((state) => state.ItemsState);
  const products = ItemsState.data;

  const hasStockRows = AllStockStock.length > 0;

  useEffect(() => {
    dispatch(fetchProducts());
    dispatch(fetchCustomers());
  }, [dispatch]);

  useEffect(() => {
    if (!directSendEnabled || !itemId) return;
    const purchase = Number(itemPurchase);
    if (Number.isFinite(purchase) && purchase > 0) {
      setDirectPrice(String(purchase));
    }
  }, [directSendEnabled, itemId, itemPurchase]);

  useEffect(() => {
    const onOnline = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    setIsOnline(typeof navigator !== "undefined" ? navigator.onLine : true);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  const handleItemCodeChange = useCallback(
    (value) => {
      setItemCode(value);
      const currentItem = findProductByInput(products, value);
      if (currentItem) {
        setItemId(resolveMongoId(currentItem._id));
        setItemCode(currentItem.code);
        setItemName(currentItem.name);
        setItemUnit(currentItem.unit || "");
        setItemCompanyId(
          resolveMongoId(currentItem.companyId?._id || currentItem.companyId)
        );
        setItemCompanyName(currentItem.companyId?.name || "");
        setItemPurchase(currentItem.purchase);
        setItemBranchId(resolveMongoId(currentItem.branchId) || "");
      } else {
        setItemId("");
        setItemName("");
        setItemUnit("");
        setItemPurchase("");
        setItemBranchId("");
      }
    },
    [products]
  );

  const handleItemCodeBlur = useCallback(
    (value) => {
      if (!findProductByInput(products, value)) {
        setItemCode("");
        setItemId("");
        setItemPurchase("");
        setItemBranchId("");
      }
    },
    [products]
  );

  const handleItemNameBlur = useCallback(
    (value) => {
      if (!findProductByInput(products, value)) {
        setItemId("");
        setItemName("");
        setItemCompanyId("");
        setItemCompanyName("");
        setItemPurchase("");
        setItemBranchId("");
      }
    },
    [products]
  );

  const clearLineFieldsAfterAdd = useCallback(() => {
    setItemCode("");
    setItemName("");
    setItemUnit("");
    setItemId("");
    setItemCompanyId("");
    setItemCompanyName("");
    setQty("");
    setDesc("");
    setDirectSendEnabled(false);
    setSelectedCustomer("");
    setCustomerInvoiceNo("");
    setCustomerDesc("");
    setDirectQty("");
    setDirectPrice("");
    setDirectPayment("");
  }, []);

  const resetFormAfterSubmit = useCallback(() => {
    setAllStockStock([]);
    setSelectedCompany("");
    setInvoiceNo("");
    setTruckNo("");
    setDesc("");
    setQty("");
    setDirectSendEnabled(false);
    setSelectedCustomer("");
    setCustomerInvoiceNo("");
    setCustomerDesc("");
    setDirectQty("");
    setDirectPrice("");
    setDirectPayment("");
  }, []);

  const handleAddToStock = useCallback(() => {
    const user = getUserData();
    if (!user) return;

    if (!SelectedCompany?._id) {
      WarningToast("Please select a company");
      return;
    }
    if (!itemId) {
      WarningToast("Please select an item");
      return;
    }
    if (itemCompanyId && SelectedCompany._id !== itemCompanyId) {
      WarningToast("Item does not belong to the selected company");
      return;
    }

    const validation = validateForm(
      {
        quantity: qty,
        date: CurDate,
      },
      ADD_STOCK_VALIDATION_RULES
    );

    if (!validation.isValid) {
      const firstError = Object.values(validation.errors)[0][0];
      WarningToast(firstError);
      return;
    }

    const invStr = String(invoiceNo).trim();
    const isDuplicate = AllStockStock.some(
      (row) =>
        String(row.itemId) === String(itemId) &&
        String(row.invoice_no) === invStr
    );

    if (isDuplicate) {
      WarningToast(
        "This item with the same invoice number already exists in the list"
      );
      return;
    }

    const sanitizedInvoiceNo = sanitizeInput.text(invoiceNo);
    const sanitizedTruckNo = sanitizeInput.text(truckNo);
    const sanitizedDesc = sanitizeInput.text(desc);
    const sanitizedQty = sanitizeInput.number(qty);

    if (directSendEnabled) {
      if (!selectedCustomer?._id) {
        WarningToast("Select customer for direct send");
        return;
      }
      if (!String(customerDesc).trim()) {
        WarningToast("Enter customer sale description");
        return;
      }
      if (!directQty || Number(directQty) <= 0) {
        WarningToast("Enter valid direct send quantity");
        return;
      }
      if (Number(directQty) > Number(sanitizedQty)) {
        WarningToast("Direct qty cannot exceed total stock qty");
        return;
      }
      if (!itemPurchase || Number(itemPurchase) <= 0) {
        WarningToast("Item purchase price is required for direct customer sale");
        return;
      }
    }

    const branchId = user.branchId || itemBranchId || "";
    if (!branchId) {
      WarningToast(
        "Branch is required: log in as a branch user or ensure the item has a branch assigned"
      );
      return;
    }

    const directSend = directSendEnabled
      ? {
          customerId: resolveMongoId(selectedCustomer._id),
          itemId: resolveMongoId(itemId),
          name: itemName,
          unit: itemUnit,
          purchase: Number(itemPurchase || 0),
          invoice_no: String(sanitizeInput.text(customerInvoiceNo)).trim(),
          desc: sanitizeInput.text(customerDesc),
          direct_qty: Number(sanitizeInput.number(directQty)),
          price: Number(itemPurchase || 0),
          payment: Number(sanitizeInput.number(directPayment) || 0),
          discount: 0,
        }
      : null;

    const payload = {
      draftId: createDraftId(),
      item: itemName,
      itemId: resolveMongoId(itemId),
      unit: itemUnit,
      company: itemCompanyName || "",
      companyId: resolveMongoId(itemCompanyId),
      qty: Number(sanitizedQty),
      purchase: Number(itemPurchase || 0),
      total_amount: Number(itemPurchase || 0) * Number(sanitizedQty),
      invoice_no: Number(sanitizedInvoiceNo),
      truck_no: sanitizedTruckNo,
      branchId,
      date: Math.floor(new Date(CurDate).getTime() / 1000),
      desc: sanitizedDesc || "-",
      directSend,
      direct_customer: directSend ? selectedCustomer.name : "-",
      direct_customer_type: directSend ? selectedCustomer.type : null,
      direct_qty: directSend ? Number(directQty) : "-",
      sale_invoice_no: directSend ? String(customerInvoiceNo) : "-",
    };

    setAllStockStock((prev) => [...prev, payload]);
    clearLineFieldsAfterAdd();
    SuccessToast("Item added to stock successfully");
  }, [
    AllStockStock,
    customerDesc,
    customerInvoiceNo,
    CurDate,
    clearLineFieldsAfterAdd,
    desc,
    directPayment,
    directPrice,
    directQty,
    directSendEnabled,
    invoiceNo,
    itemBranchId,
    itemCompanyId,
    itemCompanyName,
    itemId,
    itemName,
    itemPurchase,
    itemUnit,
    qty,
    selectedCustomer,
    SelectedCompany,
    truckNo,
  ]);

  const handleSaveDraftEdit = useCallback(
    (updated) => {
      const invStr = String(updated.invoice_no).trim();
      const isDuplicate = AllStockStock.some(
        (row) =>
          row.draftId !== updated.draftId &&
          String(row.itemId) === String(updated.itemId) &&
          String(row.invoice_no) === invStr
      );

      if (isDuplicate) {
        WarningToast(
          "This item with the same invoice number already exists in the list"
        );
        return false;
      }

      setAllStockStock((prev) =>
        prev.map((row) => (row.draftId === updated.draftId ? updated : row))
      );
      SuccessToast("Stock line updated");
      return true;
    },
    [AllStockStock]
  );

  const handleCloseEditModal = useCallback((open) => {
    setOpenEditModal(open);
    if (!open) setSelected(null);
  }, []);

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const { ok: isReachable } = await checkInternetConnection();
      if (!isReachable) {
        ErrorToast(OFFLINE_SUBMIT_HINT);
        return;
      }

      if (!Array.isArray(AllStockStock) || AllStockStock.length === 0) {
        ErrorToast("Stock must be added before submitting.");
        return;
      }

      setLoading(true);

      const result = await withErrorHandling(async () => {
        const user = getUserData();
        if (!user) throw new Error("User session expired");

        const validatedStock = AllStockStock.filter(isValidStockRow).map(
          normalizeStockRowForApi
        );

        if (validatedStock.length !== AllStockStock.length) {
          throw new Error(
            "Some items have invalid data. Please check and try again."
          );
        }

        const response = await CreateManyCompanyInvoiceApi({
          invoices: validatedStock,
        });

        if (!response?.data?.success) {
          throw new Error(
            response?.data?.error?.msg || "Server error occurred"
          );
        }

        return response.data;
      }, "AddMultipleStock onSubmit");

      if (result.success) {
        SuccessToast("All Stock added successfully");
        dispatch(fetchStockStats());
        resetFormAfterSubmit();
      } else {
        ErrorToast(result.error.userMessage);
      }

      setLoading(false);
    },
    [AllStockStock, dispatch, resetFormAfterSubmit]
  );

  const itemNameBlurHandler = useMemo(
    () => (e) => handleItemNameBlur(e.target.value),
    [handleItemNameBlur]
  );

  return (
    <div className="w-full flex flex-col pt-4 pb-8">
      <Header title="Multiple Stock" desc="Manage your Stock efficiently" />

      <div className="w-full flex flex-col border border-line bg-card shadow-soft gap-y-3 rounded-2xl overflow-hidden">
        <h1 className="text-white font-bold text-base w-full bg-gradient-to-r from-main to-mainHover text-center px-4 py-3">
          Stock Information
        </h1>

        <div className="grid grid-cols-1 gap-5 p-3">
          <div className="grid grid-cols-2 max696:grid-cols-1 gap-x-5 gap-y-5">
            <CompanySelector
              SelectedSupplier={SelectedCompany}
              setSelectedSupplier={setSelectedCompany}
            />
            <AuthInput
              Width="w-[100%]"
              label="Invoice No"
              placeholder="Enter Invoice No"
              Value={invoiceNo}
              setValue={setInvoiceNo}
              Type="number"
            />
            <AuthInput
              Width="w-[100%]"
              label="Truck No"
              placeholder="Enter Truck No"
              Value={truckNo}
              setValue={setTruckNo}
            />
            <AuthInput
              Width="w-[100%]"
              label="Date"
              placeholder="Select Date"
              Type="date"
              Value={CurDate}
              setValue={setCurDate}
            />

            <ItemCodeField
              value={itemCode}
              products={products}
              onChange={handleItemCodeChange}
              onBlur={handleItemCodeBlur}
            />

            <div className="flex flex-col flex-1">
              <input
                type="text"
                list="Item-Name"
                value={itemName}
                onChange={() => {}}
                disabled
                onBlur={itemNameBlurHandler}
                placeholder="Select Item"
                className="px-3 py-2.5 outline-none bg-subtle rounded-lg border border-line text-inkSecondary text-sm col-span-2 max1000:col-span-1"
              />
            </div>

            <AuthInput
              label="Quantity"
              placeholder="Enter Quantity"
              Type="number"
              Value={qty}
              setValue={setQty}
              Width="w-[100%]"
            />
            <AuthInput
              label="Description"
              placeholder="Enter Description"
              Value={desc}
              setValue={setDesc}
              Width="w-[100%]"
            />

            <div className="col-span-2 border-t border-main/20 pt-4">
              <label className="flex items-center gap-2 cursor-pointer mb-4">
                <input
                  type="checkbox"
                  checked={directSendEnabled}
                  onChange={(e) => setDirectSendEnabled(e.target.checked)}
                  className="w-4 h-4 accent-main"
                />
                <span className="text-sm font-bold text-ink">
                  Direct customer ko bhej di (sale invoice bhi ban jaye gi)
                </span>
              </label>

              {directSendEnabled && (
                <div className="grid grid-cols-2 max696:grid-cols-1 gap-x-5 gap-y-5 p-4 rounded-xl border border-main/30 bg-brandSoft">
                  <div className="col-span-2 max696:col-span-1">
                    <CustomerSelector
                      SelectedCustomer={selectedCustomer}
                      setSelectedCustomer={setSelectedCustomer}
                    />
                  </div>
                  <AuthInput
                    Width="w-[100%]"
                    label="Customer Sale Invoice No (optional)"
                    Named="SaleBill"
                    placeholder="Optional — auto # assigned"
                    Value={customerInvoiceNo}
                    setValue={setCustomerInvoiceNo}
                    Type="number"
                  />
                  <AuthInput
                    Width="w-[100%]"
                    label="Customer Sale Description"
                    placeholder="Enter sale description"
                    Value={customerDesc}
                    setValue={setCustomerDesc}
                  />
                  <AuthInput
                    Width="w-[100%]"
                    label={`Direct Qty (total ${qty || "—"} mein se)`}
                    placeholder="Kitna direct customer ko"
                    Type="number"
                    Value={directQty}
                    setValue={setDirectQty}
                  />
                  <AuthInput
                    Width="w-[100%]"
                    label="Sale Unit Price (purchase rate)"
                    placeholder="Purchase price"
                    Type="number"
                    Value={directPrice}
                    setValue={() => {}}
                    disabled
                  />
                  <AuthInput
                    Width="w-[100%]"
                    label="Payment (optional)"
                    placeholder="Cash received"
                    Type="number"
                    Value={directPayment}
                    setValue={setDirectPayment}
                  />
                </div>
              )}
            </div>

            <div className="flex justify-end mb-2 col-span-2">
              <button
                type="button"
                onClick={handleAddToStock}
                className="px-4 py-2 h-10 border-2 border-main bg-brandSoft text-main rounded-lg w-fit hover:bg-main hover:text-white transition-colors ease-in-out duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-bold text-sm active:scale-[.98]"
                disabled={Loading}
              >
                {Loading ? "Adding..." : "Add To Stock"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full mt-10 flex flex-col items-center">
        {hasStockRows && (
          <div className="w-full max-w-7xl">
            <SearchableTable
              SearchPlaceholder="Search Item Name..."
              SearchText={SearchText}
              setSearchText={setSearchText}
              CurrentData={AllStockStock}
              setRows={setAllStockStock}
              setOpenEditModal={setOpenEditModal}
              setSelected={setSelected}
              Columns={AllStockColumns}
            />
          </div>
        )}

        {!isOnline && hasStockRows && (
          <p className="text-center text-sm text-warning font-quicksand font-semibold px-4">
            Internet disconnected — “Add All Stock” abhi kaam nahi karega. Online
            hone par dubara try karein.
          </p>
        )}

        {hasStockRows && (
          <button
            type="button"
            onClick={onSubmit}
            title={
              !isOnline
                ? "Internet zaroori hai — pehle connection check karein"
                : undefined
            }
            className="px-4 py-2 h-10 border-2 border-main bg-main text-white rounded-lg w-fit hover:bg-mainHover transition-colors ease-in-out duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-quicksand font-bold text-sm active:scale-[.98]"
            disabled={Loading || !isOnline}
          >
            {Loading ? "Submitting..." : "Add All Stock"}
          </button>
        )}
      </div>

      {OpenEditModal && Selected && (
        <EditDraftStockModal
          open={OpenEditModal}
          setOpen={handleCloseEditModal}
          stockDetail={Selected}
          onSave={handleSaveDraftEdit}
          products={products}
        />
      )}
    </div>
  );
};

export default AddMultipleStock;
